import React from 'react';
import { useHistory } from 'react-router-dom';
import ModuleCard from './ModuleCard';
import Dv2ElearningMedia, { useImageWithFallback } from './Dv2ElearningMedia';
import {
    calcProgress,
    programHeroPair,
    resolveElearningImagePair,
} from './programConfig';
import './elearningImageV2.css';

function CourseTile({ course, onSelectCourse }) {
    const imagePair = resolveElearningImagePair({
        courseId: course.id,
        title: course.title,
        legacyPath: course.imageUrl,
    });
    const bg = useImageWithFallback(imagePair.src, imagePair.fallbackSrc);

    return (
        <button
            type="button"
            className="dv2-el__course dv2-elimg-v2"
            style={bg ? { backgroundImage: `url("${bg}")` } : undefined}
            onClick={() => onSelectCourse(course)}
        >
            <span className="dv2-el__course-title">{course.title}</span>
        </button>
    );
}

const ProgramHub = ({
    config,
    modules,
    breakthroughModules = [],
    courses = [],
    selectedCourse,
    loading,
    errorMsg,
    onOpenModule,
    onSelectCourse,
    onBackToCourses,
}) => {
    const history = useHistory();
    const progress = calcProgress(modules);
    const completed = modules.filter((m) => m.isCompleted).length;
    const hero = programHeroPair(config.id);

    if (loading) {
        return <p className="dv2-el__status">Loading program…</p>;
    }

    if (errorMsg) {
        return <p className="dv2-el__error">{errorMsg}</p>;
    }

    // ——— Additional: course picker ———
    if (config.isAdditional && !selectedCourse) {
        const purchased = (courses || []).filter((c) => c.purchased);
        if (!purchased.length) {
            return (
                <div className="dv2-el__empty">
                    <Dv2ElearningMedia
                        className="dv2-el__empty-hero dv2-elimg-v2"
                        src={hero.src}
                        fallbackSrc={hero.fallbackSrc}
                    />
                    <h3>{config.title}</h3>
                    <p>{config.phaseCopy}</p>
                    <p className="dv2-el__muted">
                        No purchased programs yet. Browse options from the main
                        dashboard when available.
                    </p>
                </div>
            );
        }
        return (
            <div className="dv2-el__courses">
                <p className="dv2-el__phase">
                    <span>{config.phaseLabel}</span> {config.phaseCopy}
                </p>
                <div className="dv2-el__course-grid">
                    {purchased.map((course) => (
                        <CourseTile
                            key={course.id}
                            course={course}
                            onSelectCourse={onSelectCourse}
                        />
                    ))}
                </div>
            </div>
        );
    }

    // ——— Path gate: need 12 breakthrough unlocked ———
    if (config.requiresBreakthroughUnlocked) {
        const unlockedBt = breakthroughModules.filter((m) => m.isUnlocked)
            .length;
        if (unlockedBt < config.requiresBreakthroughUnlocked) {
            return (
                <div className="dv2-el__empty">
                    <Dv2ElearningMedia
                        className="dv2-el__empty-hero dv2-elimg-v2"
                        src={hero.src}
                        fallbackSrc={hero.fallbackSrc}
                    />
                    <h3>{config.title}</h3>
                    <p>{config.gateCopy}</p>
                    <p className="dv2-el__phase">
                        <span>{config.phaseLabel}</span> {config.phaseCopy}
                    </p>
                </div>
            );
        }
    }

    // ——— Empty / quiz CTA ———
    if (!modules.length) {
        const quizPath =
            typeof config.quizPath === 'function' && selectedCourse
                ? config.quizPath(selectedCourse.id)
                : config.quizPath;

        return (
            <div className="dv2-el__empty">
                {config.isAdditional && selectedCourse ? (
                    <button
                        type="button"
                        className="dv2-el__back-link"
                        onClick={onBackToCourses}
                    >
                        ← All programs
                    </button>
                ) : null}
                <Dv2ElearningMedia
                    className="dv2-el__empty-hero dv2-elimg-v2"
                    src={hero.src}
                    fallbackSrc={hero.fallbackSrc}
                />
                <h3>{selectedCourse?.title || config.title}</h3>
                <p className="dv2-el__phase">
                    <span>{config.phaseLabel}</span> {config.phaseCopy}
                </p>
                {quizPath ? (
                    <button
                        type="button"
                        className="dv2-el__cta"
                        onClick={() => history.push(quizPath)}
                    >
                        {config.quizCta}
                    </button>
                ) : null}
            </div>
        );
    }

    return (
        <div className="dv2-el__hub">
            {config.isAdditional && selectedCourse ? (
                <button
                    type="button"
                    className="dv2-el__back-link"
                    onClick={onBackToCourses}
                >
                    ← All programs
                </button>
            ) : null}
            <div className="dv2-el__hub-head">
                <div>
                    <h3 className="dv2-el__hub-title">
                        {selectedCourse?.title || config.title}: {completed}/
                        {modules.length}
                    </h3>
                    <p className="dv2-el__phase">
                        <span>{config.phaseLabel}</span> {config.phaseCopy}
                    </p>
                </div>
                <div
                    className="dv2-el__hub-progress"
                    aria-label={`${progress}% complete`}
                >
                    <div className="dv2-el__hub-bar">
                        <span style={{ width: `${progress}%` }} />
                    </div>
                    <span>{progress}%</span>
                </div>
            </div>
            <div className="dv2-el__module-grid">
                {modules.map((mod, index) => (
                    <ModuleCard
                        key={mod.id}
                        module={mod}
                        index={index}
                        onOpen={onOpenModule}
                    />
                ))}
            </div>
        </div>
    );
};

export default ProgramHub;
